<template>
  <div>
    <nav
      id="nav"
      v-if="showMenu"
      v-bind:class="{ showMobileMenu, hideMobileMenu: !showMobileMenu, smallMenu: smallMenu }"
    >
      <div class="mobileHeader">
        <button class="borderless toggler" v-on:click="toggleMobileMenu()"></button>
        <span class="pageTitle">{{ $route.meta.title }}</span>
      </div>
      <ul class="main" @click="toggleMobileMenu()">
        <li v-if="isAdminOrEditor">
          <router-link :to="{ name: 'adminHome' }">Painel</router-link>
        </li>
        <li v-else>
          <router-link :to="{ name: 'memberHome' }">Painel</router-link>
        </li>
        <!-- #469 — right after Painel because during onboarding it is the only
             thing there is to do, and it disappears for good once the setting is
             switched off. The v-if hides the menu; it does NOT protect the
             endpoint (meta.access is cosmetic, there is no route guard) — that
             gate is onboardingEnabled() in the API.
             
             AFTER the v-if/v-else pair above, never between them: an element
             placed in the middle takes the `v-else` for its own, and the two
             Painel entries then render together whenever this condition is
             false — which is every organisation, since hasOnboarding ships
             false. -->
        <li v-if="isAdminOrEditor && hasOnboarding">
          <router-link :to="{ name: 'onboarding' }">Onboarding</router-link>
        </li>
        <li v-if="isAdminOrEditor">
          <router-link :to="{ name: 'membersList' }"
            >{{ orgTerms.memberPlural }}
            <span
              class="notificationCount"
              v-if="memberApplicationsTotal !== 0"
              title="há candidaturas à espera de resposta"
            >
              !
            </span>
          </router-link>
        </li>

        <li v-if="isAdminOrEditor">
          <router-link :to="{ name: 'membershipCardList' }"
            >Cartões de {{ orgTerms.memberSingular }}</router-link
          >
        </li>
        <li v-if="hasTreasury && showTreasury !== 'false' && isAdminOrEditor">
          <router-link :to="{ name: 'treasuryMain' }">Tesouraria
            <span
              class="notificationCount"
              v-if="expenseSubmissionsAttentionTotal > 0"
              title="há despesas pendentes de aprovação"
            >
              !
            </span>
          </router-link>
        </li>
        <li v-if="hasExpenseSubmissions && memberCanSubmitExpenses && (isMemberView || !isAdminOrEditor)">
          <router-link :to="{ name: 'expenseSubmissionList' }">Despesas
            <span
              class="notificationCount"
              v-if="expenseSubmissionsAttentionTotal > 0"
              title="há despesas que precisam da sua atenção"
            >
              {{ expenseSubmissionsAttentionTotal }}
            </span>
          </router-link>
        </li>
        <li v-if="hasInvoiceRequests && (isMemberView || !isAdminOrEditor)">
          <router-link :to="{ name: 'invoiceRequestList' }">{{ invoiceRequestLabels.plural }}
            <span
              class="notificationCount"
              v-if="invoiceRequestsPendingTotal > 0"
              :title="invoiceRequestLabels.plural.toLowerCase() + ' em processo'"
            >
              {{ invoiceRequestsPendingTotal }}
            </span>
          </router-link>
        </li>
        <li v-if="hasKmMaps && memberCanSubmitKmMaps && (isMemberView || !isAdminOrEditor)">
          <router-link :to="{ name: 'kmMapList' }">Mapas de Deslocação</router-link>
        </li>
        <li v-if="hasCostCenterTransfers && (isMemberView || !isAdminOrEditor)">
          <router-link :to="{ name: 'costCenterTransferList' }">Transferências</router-link>
        </li>
        <li v-if="orgType === 'association' && isAdminOrEditor">
          <router-link :to="{ name: 'reportOverview' }">Relatórios</router-link>
        </li>
        <li v-if="isAdminOrEditor">
          <router-link :to="{ name: 'messageList' }">Mensagens</router-link>
        </li>
        <!-- Navigate on the whole <li>: the inner link doesn't fill the row, so a tap
             on the li's edge could miss it and not navigate (notably Firefox mobile). -->
        <li class="settings" v-if="isAdminOrEditor" @click="$router.push({ name: 'userSettings' })">
          <router-link :to="{ name: 'userSettings' }">
            <IconSettings stroke="1.25" size="20" color="white" />
            <span class="mobileText"> Definições </span>
          </router-link>
        </li>
      </ul>
      <div v-if="canToggleViewMode" class="viewToggle">
        <div class="viewToggleSwitch">
          <button
            class="button borderless viewToggleOption"
            :class="{ active: !isMemberView }"
            @click="switchToAdmin()"
          >
            Admin
          </button>
          <button
            class="button borderless viewToggleOption"
            :class="{ active: isMemberView }"
            @click="switchToMember()"
          >
            {{ orgTerms.memberSingular }}
          </button>
        </div>
      </div>
      <ul id="rightNav">
        <li v-if="isLoggedin">
          <NotificationBell />
        </li>
        <li><UserMenu></UserMenu></li>
      </ul>
    </nav>
    <nav
      id="secondaryNav"
      class="smallerMenu"
      v-else-if="this.$route.meta.module === 'settings'"
    ></nav>
    <nav id="secondaryNav" class="smallerMenu" v-if="this.$route.meta.module === 'members'">
      <ul>
        <li v-if="hasMemberApplication && showMemberApplication !== 'false' && isAdminOrEditor">
          <router-link :to="{ name: 'memberApplicationsList' }"
            >Candidaturas
            <span class="notificationCount" v-if="memberApplicationsTotal !== 0">{{
              memberApplicationsTotal
            }}</span></router-link
          >
        </li>
        <li v-if="isAdminOrEditor">
          <router-link :to="{ name: 'membersList' }">{{ orgTerms.memberPlural }}</router-link>
        </li>
        <li>
          <router-link :to="{ name: 'paymentsList' }"
            >Pagamentos de {{ orgTerms.membershipPayments }}</router-link
          >
        </li>
        <li>
          <router-link :to="{ name: 'unpaid' }">
            Dívidas de {{ orgTerms.membershipPayments }}</router-link
          >
        </li>
      </ul>
    </nav>
    <nav
      id="secondaryNav"
      class="smallerMenu"
      v-if="this.$route.meta.module === 'membershipCards' && showMenu"
    >
      <ul>
        <li><router-link :to="{ name: 'membershipCardList' }">Criar</router-link></li>
        <li>
          <router-link :to="{ name: 'membershipCardVerifyMultiple' }">Verificar</router-link>
        </li>
      </ul>
    </nav>
    <nav
      id="secondaryNav"
      class="smallerMenu"
      v-if="this.$route.meta.module === 'settings' && isAdminOrEditor"
    >
      <ul>
        <li><router-link :to="{ name: 'userSettings' }">Utilizadores</router-link></li>
        <li>
          <router-link :to="{ name: 'memberSettings' }">{{ orgTerms.memberPlural }}</router-link>
        </li>
        <li v-if="showMemberApplication !== 'false'">
          <router-link :to="{ name: 'memberApplicationSettings' }">Candidaturas</router-link>
        </li>
        <li>
          <router-link :to="{ name: 'membershipCardSettings' }">Cartões</router-link>
        </li>
        <li><router-link :to="{ name: 'messageSettings' }">Mensagens</router-link></li>
        <li v-if="showTreasury !== 'false'">
          <router-link :to="{ name: 'treasuryDocSettings' }">Tesouraria</router-link>
        </li>
        <li><router-link :to="{ name: 'developers' }">Programadores</router-link></li>
        <li>
          <router-link :to="{ name: 'changeLogList' }">Histórico de Alterações</router-link>
        </li>
        <li v-if="hasSheetSync">
          <router-link :to="{ name: 'sheetSyncFindings' }">Importação das folhas</router-link>
        </li>
      </ul>
    </nav>

    <nav
      id="secondaryNav"
      class="smallerMenu"
      v-else-if="this.$route.meta.module === 'treasury' && isAdminOrEditor && loadedSettings"
    >
      <ul>
        <li>
          <router-link :to="{ name: transactionRouteName }">Transacções</router-link>
        </li>
        <li v-if="treasuryHasCostCenters">
          <router-link :to="{ name: 'costCenterList' }">Centros de Custo</router-link>
        </li>
        <li v-if="taskDistributionVisible">
          <router-link :to="{ name: 'costCenterManagers' }">Distribuição de Tarefas</router-link>
        </li>
        <li>
          <router-link :to="{ name: 'externalEntityList' }">Clientes</router-link>
        </li>
        <li v-if="hasExpenseSubmissions">
          <router-link :to="{ name: 'expenseSubmissionList' }">Despesas
            <span class="notificationCount" v-if="expenseSubmissionsAttentionTotal > 0">{{
              expenseSubmissionsAttentionTotal
            }}</span>
          </router-link>
        </li>
        <li v-if="hasCostCenterTransfers">
          <router-link :to="{ name: 'costCenterTransferList' }">Transferências</router-link>
        </li>
        <li v-if="hasInvoiceRequests">
          <router-link :to="{ name: 'invoiceRequestList' }">{{ invoiceRequestLabels.plural }}
            <span
              class="notificationCount"
              v-if="invoiceRequestsPendingTotal > 0"
              :title="invoiceRequestLabels.plural.toLowerCase() + ' em processo'"
            >
              {{ invoiceRequestsPendingTotal }}
            </span>
          </router-link>
        </li>
        <li v-if="hasKmMaps">
          <router-link :to="{ name: 'kmMapList' }">Mapas de Deslocação
            <span
              class="notificationCount"
              v-if="kmMapsPendingTotal > 0"
              title="mapas de deslocação pendentes de aprovação"
            >
              {{ kmMapsPendingTotal }}
            </span>
          </router-link>
        </li>
        <li v-if="hasTransactionImporter">
          <router-link :to="{ name: 'transactionImporterList' }">Importador</router-link>
        </li>
        <!--li><router-link :to="{ name: 'treasuryDocList' }">Documentos</router-link></li-->
      </ul>
    </nav>
  </div>
</template>

<script>
import UserMenu from './UserMenu.vue'
import NotificationBell from '../Notifications/NotificationBell.vue'
import { mapState, mapGetters } from 'vuex'
import { orgTerms } from '@/terminology'
import { invoiceRequestLabels } from '@/lib/invoiceRequestLabels'
import { IconSettings } from '@tabler/icons-vue'
import { COUNT_SOURCES } from '@/store/actionableCounts'
import { transferMode } from '@/lib/costCenterTransfers'

export default {
  name: 'NavigationC',
  components: { UserMenu, NotificationBell, IconSettings },
  data() {
    return {
      orgTerms: orgTerms,
      invoiceRequestLabels,
      showTreasury: import.meta.env.VITE_SHOW_TREASURY,
      showMemberApplication: import.meta.env.VITE_SHOW_MEMBER_APPLICATION,
      showMobileMenu: false,
      smallMenu: true,
    }
  },
  computed: {
    ...mapState({
      settings: (state) => state.settings.list.default.data,
      memberApplicationsTotal: (state) => state.memberApplications.list.menuIcon?.total || 0,
      expenseSubmissionsAttentionTotal: (state) => state.expenseSubmissions.list.attentionCount?.total || 0,
      invoiceRequestsPendingTotal: (state) => state.invoiceRequests.list.pendingCount?.total || 0,
      kmMapsPendingTotal: (state) => state.kmMaps.list.pendingCount?.total || 0,
      loadedSettings: (state) => state.settings.list.default.loaded,
    }),
    ...mapGetters({
      isLoggedin: 'auth/isLoggedin',
      isAdminOrEditor: 'auth/isAdminOrEditor',
      isAdmin: 'auth/isAdmin',
      canToggleViewMode: 'auth/canToggleViewMode',
      isMemberView: 'auth/isMemberView',
      orgType: 'settings/orgType',
      settingsByName: 'settings/listByName',
      taskDistributionVisible: 'settings/taskDistributionVisible',
    }),
    transactionRouteName() {
      return import.meta.env.VITE_CLIENT === 'pronobis' ? 'pronobisTransactionList' : 'transactionList'
    },
    // #467 — the sheet import exists only in Minga's build, and only admins read it.
    hasSheetSync() {
      return import.meta.env.VITE_CLIENT === 'minga' && this.isAdmin
    },
    hasTransactionImporter() {
      return this.settingsByName.transactionImporterEnabled?.value === 'true'
    },
    hasMemberApplication() {
      if (typeof this.$store.getters['settings/listByName'].hasMemberApplication === 'undefined') {
        return false
      }
      if (this.$store.getters['settings/listByName'].hasMemberApplication.value === 'true') {
        return true
      } else {
        return false
      }
    },
    hasTreasury() {
      const hasTreasurySetting = this.settings.find((s) => {
        return s.name === 'hasTreasury'
      })
      if (!hasTreasurySetting) {
        return false
      }
      return hasTreasurySetting.value === 'true'
    },
    treasuryHasCostCenters() {
      if (this.settingsByName.treasuryHasCostCenters?.value === 'true') {
        return true
      }
      return false
    },
    hasExpenseSubmissions() {
      return this.settingsByName.hasExpenseSubmissions?.value === 'true'
    },
    hasOnboarding() {
      return this.settingsByName.hasOnboarding?.value === 'true'
    },
    hasInvoiceRequests() {
      return this.settingsByName.hasInvoiceRequests?.value === 'true'
    },
    // #514 — on whenever the organisation has transfers on: the receiving
    // side has things to see here too, not only those who can send.
    hasCostCenterTransfers() {
      return transferMode(this.settingsByName) !== 'off'
    },
    hasKmMaps() {
      return this.settingsByName.hasKmMaps?.value === 'true'
    },
    memberCanSubmitExpenses() {
      return this.settingsByName.expenseSubmissionMembersCanSubmit?.value === 'true'
    },
    memberCanSubmitKmMaps() {
      return this.settingsByName.kmMapsCanSubmit?.value === 'true'
    },
    showMenu() {
      let showMenu = false
      if (this.isLoggedin) {
        showMenu = true
      }
      if (['recover', 'unpaidSingle'].includes(this.$route.name)) {
        showMenu = false
      }
      return showMenu
    },
  },
  methods: {
    switchToAdmin() {
      if (this.isMemberView) {
        this.$store.commit('auth/setViewMode', null)
        this.$router.push({ name: 'adminHome' })
      }
    },
    switchToMember() {
      if (!this.isMemberView) {
        this.$store.commit('auth/setViewMode', 'member')
        this.$router.push({ name: 'memberHome' })
      }
    },
    toggleMobileMenu() {
      this.showMobileMenu = !this.showMobileMenu
    },
    hideMobileMenu() {
      this.showMobileMenu = false
    },
    hideMobileMenuOnResize() {
      this.hideMobileMenu()
    },
  },
  watch: {
    // A count behind `isOn` is not fetched while its feature reads as off, and
    // before the settings arrive every feature reads as off — so a refresh at
    // mount could skip a count for good. Refresh again once they are in.
    loadedSettings(loaded) {
      if (loaded) this.$store.dispatch('actionableCounts/refresh')
    },
  },
  unmounted() {
    window.removeEventListener('resize', this.hideMobileMenuOnResize)
  },
  created() {
    // Create the count lists synchronously before first render so the inline
    // count reads below are safe. List names come from the shared descriptor.
    COUNT_SOURCES.forEach((s) => this.$store.dispatch(`${s.module}/init`, s.listName))
  },
  mounted() {
    // Shared refetch path — the bell and the ws:counts:refresh handler use the
    // same action and the same COUNT_SOURCES wiring, so the inline counts and the
    // bell badge load from one place.
    this.$store.dispatch('actionableCounts/refresh')

    window.addEventListener('resize', this.hideMobileMenuOnResize)
  },
}
</script>

<style lang="scss" scoped>
@use '@/scss/_variables.scss';
nav {
  display: flex;
  position: relative;
  transition: all 0.25s;
  font-weight: 300;
  min-height: 35px;
  :deep(button) {
    font-weight: 300;
  }
  .router-link-active {
    font-weight: 500;
  }
}
a {
  text-decoration: none;
}

#nav {
  background: var(--slightly-darker-primary) !important;
  font-size: 0.81rem;
  overflow: visible !important;
  justify-content: space-between;
  .toggler {
    margin: 0;
    padding: 0;
    width: 20px;
    height: 20px;
    min-width: 20px;
    min-height: 20px;
    color: white;
    background: url('/src/assets/bars-solid.svg') transparent no-repeat center;
    background-size: 20px 20px;
    display: block;
  }
  .pageTitle {
    color: white;
    font-weight: bold;
  }
  .toggler:hover::before {
    background-color: transparent !important;
  }
}

@media only screen and (max-width: calc(1000px - 1px)) {
  .mobileHeader {
    display: flex;
    align-items: center;
    gap: calc(var(--sp) / 2);
    padding-left: var(--sp);
    line-height: 35px;
  }
  #nav .pageTitle {
    display: block;
  }
  .showMobileMenu {
    .main {
      display: flex;
      flex-direction: column;
      width: 100%;
      text-align: center;
      padding-bottom: var(--sp);
      li {
        padding-top: calc(var(--sp) * 1);
        padding-bottom: calc(var(--sp) * 1);
        width: 100%;
      }
    }
  }
  .hideMobileMenu {
    > .main {
      display: none;
    }
  }
  #nav {
    flex-wrap: wrap;
    gap: 0;
    align-items: center;
    .main {
      flex-direction: column;
      width: 100%;
      order: 4;
      li {
        width: 100%;
        margin-top: calc(var(--sp) / 2);
        margin-bottom: calc(var(--sp) / 2);
      }
    }
    #rightNav {
      flex-direction: row !important;
      flex-shrink: 0;
    }
  }
  #rightNav li {
    max-height: initial !important;
  }
}

@media only screen and (min-width: 1000px) {
  .mobileHeader {
    display: none;
  }
}

.main {
}
ul {
  list-style: none;
  margin: 0;
  padding-left: 0;
  display: flex;
  align-items: center;
  justify-content: left;
  li {
    max-height: 20px;
    margin-left: calc(var(--sp) / 2);
    margin-right: calc(var(--sp) / 2);
    padding: 0 calc(var(--sp) / 2);
    a {
      color: white;
    }
    a.router-link-active {
      text-decoration: none;
    }
  }
}

#rightNav {
  position: relative;
  gap: var(--sp);
  li {
    padding-top: 0 !important;
  }
}
@media only screen and (max-width: 1000px) {
  #rightNav {
    padding-right: 0;
  }
}

#rightNav > li {
  margin-left: 0;
  margin-right: 0;
  padding-left: 0;
  padding-right: 0;
  padding-bottom: 0;
  padding-top: 0;
}

#rightNav > li > ul > li {
  display: block;
  margin-left: 0;
  margin-right: 0;
  padding-left: 0;
  padding-right: 0;
  white-space: nowrap;
}
#rightNav > li > ul > li > ul {
  flex-direction: column;
  z-index: 1;
  padding-top: calc(var(--sp) / 2);
  padding-bottom: var(--sp);
}
#rightNav > li > ul > li > ul > li {
  padding-top: var(--sp);
  padding-bottom: 0;
}

.viewToggle {
  max-height: none !important;
  border: none;
  display: flex;
  align-items: center;
  gap: 1px;
  background: transparent;
}
.viewToggleSwitch {
  display: flex;
}
.viewToggleOption {
  padding: 3px 10px !important;
  min-height: 20px;
  min-width: 0 !important;
  font-size: 0.8rem;
  color: white !important;
  background: var(--primary);
  border: none;
  border-radius: 0;
  cursor: pointer;
  transition: all 0.15s;
  margin: 0;
  line-height: 1.4;
  white-space: nowrap;
  &:not(.active) {
    background: var(--primary);
    cursor: pointer;
  }
  &:hover:not(.active) {
    opacity: 1;
    background: var(--light-primary);
  }
  &.active {
    cursor: default;
    background: var(--dark-primary);
  }
  &::before {
    top: 1px !important;
    left: 0 !important;
    border-radius: 0 !important;
    border: none !important;
    box-shadow: none !important;
    background-color: transparent !important;
  }
  &:hover::before {
    top: 1px !important;
    left: 0 !important;
    background-color: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }
}
.settings {
  a {
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

@media print {
  nav {
    display: none;
  }
}

.mobileText {
  display: none;
}

@media only screen and (max-width: 1000px) {
  .mobileText {
    display: inline-block;
    padding-left: var(--sp);
    color: white;
  }
}

#secondaryNav {
  background-color: var(--header-background) !important;
  border-bottom: 1px solid var(--header-background-darker);
  box-shadow: var(--default-shadow);
  min-height: 40px;
  ul {
    flex-wrap: wrap;
    gap: calc(var(--sp) * 1.5);

    padding: calc(var(--sp) / 2) var(--sp);
    li {
      margin: 0;
      padding: 0;
    }
  }
}
</style>

<style lang="scss">
#nav .main button {
  white-space: nowrap;
}
</style>
